<template>
  <div v-if="lesson" class="cook" @pointerdown.once="primeAudio">
    <!-- Mise en place: everything before the heat. -->
    <section v-if="phase === 'mise'" class="page stack">
      <p class="eyebrow">Lesson {{ lesson.number }} · Before any heat</p>
      <h1>Mise en place</h1>
      <p class="muted">Get every one of these done and within reach. Once the pan is hot there is no time to look for the garlic.</p>
      <label v-for="(item, i) in lesson.mise" :key="i" class="check card" :class="{ 'check--done': mise[i] }">
        <input v-model="mise[i]" type="checkbox" class="check__box">
        <span class="check__text">{{ item }}</span>
      </label>
      <button type="button" class="btn btn-primary btn-lg btn-block" @click="startSteps">
        {{ allMise ? 'Everything’s ready. Start cooking' : `Start anyway (${miseDone}/${lesson.mise.length} done)` }}
      </button>
    </section>

    <!-- The steps, one screen each. -->
    <section v-else-if="phase === 'steps'" class="page stack cook__step">
      <p class="eyebrow">Step {{ stepIndex + 1 }} of {{ lesson.steps.length }} · {{ lesson.dish }}</p>
      <h1 class="cook__text">{{ step.text }}</h1>
      <p v-if="step.tip" class="cook__tip"><strong>Why:</strong> {{ step.tip }}</p>
      <p v-if="step.check" class="cook__check"><strong>Done looks like:</strong> {{ step.check }}</p>

      <div v-if="step.timer" class="row">
        <button v-if="!timerForStep(stepIndex)" type="button" class="btn btn-primary btn-lg" @click="startTimer(step.timer, stepIndex)">
          Start {{ formatSeconds(step.timer.seconds) }} timer · {{ step.timer.label }}
        </button>
        <span v-else class="pill pill--green">Timer running · {{ step.timer.label }}</span>
      </div>

      <AskBox :lesson="lesson" :step-index="stepIndex"/>

      <div class="cook__nav">
        <button type="button" class="btn btn-lg" :disabled="stepIndex === 0" @click="go(-1)">Back</button>
        <button v-if="stepIndex < lesson.steps.length - 1" type="button" class="btn btn-primary btn-lg" @click="go(1)">Next</button>
        <button v-else type="button" class="btn btn-success btn-lg" @click="finish">It’s on the plate</button>
      </div>
      <ol class="cook__dots" aria-hidden="true">
        <li v-for="(s, i) in lesson.steps" :key="i" :class="{ on: i === stepIndex, past: i < stepIndex }" @click="stepIndex = i"></li>
      </ol>
      <button type="button" class="cook__quit" @click="quit">Leave cook mode</button>
    </section>

    <!-- Done. -->
    <section v-else class="page stack">
      <p class="eyebrow">Lesson {{ lesson.number }}</p>
      <h1>On the plate.</h1>
      <p class="muted">{{ lesson.plate }}</p>
      <p>Eat. Then, when everyone’s done, one button and everybody says what they thought.</p>
      <router-link :to="`/debrief/${lesson.id}`" class="btn btn-primary btn-lg btn-block">We ate. Talk.</router-link>
      <router-link to="/" class="btn btn-block">Later</router-link>
    </section>

    <TimerBar :timers="visibleTimers" @extend="extendTimer" @dismiss="dismissTimer"/>
  </div>
</template>

<script>
// Cook mode. Big type, one step per screen, timers the app owns, the
// screen kept awake, and Claude a tap away. State survives a reload
// (sessionStorage): a phone locked mid-step comes back where it was, and
// timers are wall-clock so they come back right too.
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import TimerBar from './TimerBar.vue';
import AskBox from './AskBox.vue';
import { lessonById } from '../curriculum/lessons.js';
import { createTimer, extend, isDone, formatSeconds, chime, primeAudio, notifyDone } from '../utils/timers.js';
import { markLesson } from '../utils/course.js';
import { setActivity, setAppError } from '../state.js';

const SESSION_KEY = (id) => `enjoyCookingCook:${id}`;

export default {
  name: 'CookScreen',
  components: { TimerBar, AskBox },
  props: { id: { type: String, required: true }, hat: { type: String, default: null } },
  setup (props) {
    const router = useRouter();
    const lesson = computed(() => lessonById(props.id));
    const phase = ref('mise');
    const mise = ref([]);
    const stepIndex = ref(0);
    const timers = ref([]);
    const rung = new Set();
    let wakeLock = null;
    let watchTick = null;
    setActivity('cooking');

    // --- persistence -------------------------------------------------------
    function restore () {
      try {
        const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY(props.id)) || 'null');
        if (saved) {
          phase.value = saved.phase || 'mise';
          mise.value = saved.mise || [];
          stepIndex.value = saved.stepIndex || 0;
          timers.value = saved.timers || [];
        }
      } catch { /* start fresh */ }
      if (!mise.value.length) mise.value = (lesson.value?.mise || []).map(() => false);
    }
    function persist () {
      try {
        sessionStorage.setItem(SESSION_KEY(props.id), JSON.stringify({ phase: phase.value, mise: mise.value, stepIndex: stepIndex.value, timers: timers.value }));
      } catch { /* nothing */ }
    }
    watch([phase, mise, stepIndex, timers], persist, { deep: true });

    // --- mise ----------------------------------------------------------------
    const miseDone = computed(() => mise.value.filter(Boolean).length);
    const allMise = computed(() => lesson.value && miseDone.value === lesson.value.mise.length);
    async function startSteps () {
      phase.value = 'steps';
      stepIndex.value = 0;
      if (props.hat) markLesson(props.hat, lesson.value.id, { startedAt: Date.now() }).catch(() => {});
    }

    // --- steps ---------------------------------------------------------------
    const step = computed(() => lesson.value?.steps[stepIndex.value] || {});
    const go = (delta) => { stepIndex.value = Math.min(lesson.value.steps.length - 1, Math.max(0, stepIndex.value + delta)); window.scrollTo({ top: 0 }); };

    // --- timers --------------------------------------------------------------
    const visibleTimers = computed(() => timers.value.filter((t) => !t.dismissed));
    const timerForStep = (i) => visibleTimers.value.find((t) => t.stepIndex === i);
    function startTimer (spec, i) {
      primeAudio();
      timers.value = [...timers.value, createTimer({ label: spec.label, seconds: spec.seconds, stepIndex: i })];
    }
    function extendTimer (id, seconds) {
      timers.value = timers.value.map((t) => (t.id === id ? extend(t, seconds) : t));
      rung.delete(id);
    }
    function dismissTimer (id) {
      timers.value = timers.value.map((t) => (t.id === id ? { ...t, dismissed: true } : t));
    }
    function watchTimers () {
      for (const t of visibleTimers.value) {
        if (isDone(t) && !rung.has(t.id)) {
          rung.add(t.id);
          chime();
          notifyDone(t);
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        }
      }
    }

    // --- the screen stays on -------------------------------------------------
    async function keepAwake () {
      try {
        if ('wakeLock' in navigator && !wakeLock) wakeLock = await navigator.wakeLock.request('screen');
      } catch { /* not allowed; nothing to do */ }
    }
    function onVisible () { if (document.visibilityState === 'visible') keepAwake(); }

    // --- finish / quit -------------------------------------------------------
    async function finish () {
      phase.value = 'done';
      timers.value = [];
      try {
        if (props.hat) await markLesson(props.hat, lesson.value.id, { cookedAt: Date.now() });
      } catch (error) {
        setAppError(`Cooked, but could not save it: ${error.message}`);
      }
    }
    function quit () {
      try { sessionStorage.removeItem(SESSION_KEY(props.id)); } catch { /* nothing */ }
      router.push(`/lesson/${props.id}`);
    }

    onMounted(() => {
      restore();
      keepAwake();
      document.addEventListener('visibilitychange', onVisible);
      watchTick = setInterval(watchTimers, 1000);
    });
    onBeforeUnmount(() => {
      clearInterval(watchTick);
      document.removeEventListener('visibilitychange', onVisible);
      wakeLock?.release?.().catch?.(() => {});
    });

    return { lesson, phase, mise, miseDone, allMise, startSteps, step, stepIndex, go, visibleTimers, timerForStep, startTimer, extendTimer, dismissTimer, finish, quit, formatSeconds, primeAudio };
  }
};
</script>

<style scoped>
.cook { padding-bottom: 6rem; }
.check { display: flex; gap: 0.9rem; align-items: flex-start; cursor: pointer; }
.check--done { opacity: 0.55; text-decoration: line-through; }
.check__box { width: 1.6rem; height: 1.6rem; margin: 0.15rem 0 0; accent-color: var(--green); flex: 0 0 auto; }
.check__text { font-size: 1.05rem; }
.cook__step { min-height: 70vh; }
.cook__text { font-size: 1.7rem; line-height: 1.3; }
.cook__tip { background: var(--accent-soft); border-radius: var(--radius); padding: 0.7rem 0.9rem; }
.cook__check { background: var(--green-soft); border-radius: var(--radius); padding: 0.7rem 0.9rem; }
.cook__nav { display: grid; grid-template-columns: 1fr 2fr; gap: 0.75rem; margin-top: 0.5rem; }
.cook__dots { list-style: none; display: flex; gap: 0.35rem; justify-content: center; padding: 0; margin: 0.25rem 0 0; }
.cook__dots li { width: 0.6rem; height: 0.6rem; border-radius: 50%; background: var(--line); cursor: pointer; }
.cook__dots li.past { background: var(--green); }
.cook__dots li.on { background: var(--accent); transform: scale(1.3); }
.cook__quit { background: none; border: 0; color: var(--muted); font-size: 0.85rem; padding: 0.5rem; }
@media (min-width: 700px) { .cook__text { font-size: 2.1rem; } }
</style>
